import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getRecentUploads } from "../../services/mediaLibraryService";
import MediaCard from "./MediaCard";
import MediaLibraryPageSizeSelect from "./MediaLibraryPageSizeSelect";
import MediaLibraryPaginationBar from "./MediaLibraryPaginationBar";
import { DEFAULT_MEDIA_PAGE_SIZE } from "./mediaLibraryPagination";

const extractList = (res) => {
  const data = res?.data;
  if (Array.isArray(data)) return data;
  const result = data?.result ?? data?.Result ?? data?.data ?? data?.Data;
  if (Array.isArray(result)) return result;
  return result?.items ?? result?.Items ?? data?.items ?? data?.Items ?? [];
};

const nullSafeText = (value) => (value == null ? "" : String(value));

/**
 * Defensive normalizer — the media-library API field names are not fully
 * locked yet, so we map the most common variations to a stable shape.
 */
const normalizeMedia = (raw) => {
  if (!raw) return null;
  return {
    mediaAssetId:
      raw.mediaAssetId ?? raw.id ?? raw._id ?? raw.mediaId ?? raw.assetId,
    mediaUrl: raw.mediaUrl ?? raw.url ?? raw.fileUrl ?? raw.path ?? "",
    thumbnailUrl:
      raw.thumbnailUrl ??
      raw.thumbUrl ??
      raw.thumbnail ??
      raw.mediaUrl ??
      raw.url ??
      "",
    previewUrl: raw.previewUrl ?? raw.previewURL ?? "",
    mediaName:
      raw.mediaName ?? raw.name ?? raw.fileName ?? raw.title ?? "Untitled",
    providerType: raw.providerType ?? raw.provider ?? raw.source ?? "Upload",
    width: raw.width ?? raw.imageWidth ?? raw.dimensions?.width ?? null,
    height: raw.height ?? raw.imageHeight ?? raw.dimensions?.height ?? null,
    fileSize: raw.fileSize ?? raw.size ?? raw.sizeInBytes ?? raw.bytes ?? null,
    altText: nullSafeText(raw.altText ?? raw.alt_text ?? raw.alt),
    caption: nullSafeText(raw.caption),
    tags: Array.isArray(raw.tags)
      ? raw.tags.join(", ")
      : nullSafeText(raw.tags),
    description: nullSafeText(raw.description),
  };
};

/**
 * Recent Uploads tab — loads the latest media assets, allows a client-side
 * name search, and renders them in a responsive card grid.
 */
const RecentUploadsTab = ({
  show = true,
  refreshKey = 0,
  highlightId = null,
  onSelect,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_MEDIA_PAGE_SIZE);

  const fetchUploads = useCallback(async () => {
    setLoading(true);
    setItems([]);
    try {
      const res = await getRecentUploads();
      const body = res?.data;
      if (body?.isSuccess === false || body?.IsSuccess === false) {
        toast.error("Unable to load recent uploads.");
        setItems([]);
        return;
      }
      const list = extractList(res).map(normalizeMedia).filter(Boolean);
      setItems(list);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Unable to load recent uploads."
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    fetchUploads();
  }, [show, fetchUploads, refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, refreshKey]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((m) =>
      (m.mediaName || "").toLowerCase().includes(term)
    );
  }, [items, search]);

  const totalEntries = filtered.length;
  const totalPages = totalEntries > 0 ? Math.ceil(totalEntries / pageSize) : 0;

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handlePageChange = (next) => {
    if (next < 1 || (totalPages > 0 && next > totalPages)) return;
    setPage(next);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  return (
    <div className="xc-ml-recent">
      <div className="xc-ml-toolbar">
        <div className="xc-ml-search">
          <i className="ri-search-line" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search media by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="xc-ml-toolbar__count">
          {loading ? "Loading..." : `${totalEntries} item(s)`}
        </span>
        <span className="xc-ml-toolbar__spacer" />
        <MediaLibraryPageSizeSelect
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </div>

      {loading ? (
        <div className="xc-ml-grid">
          {Array.from({ length: pageSize }).map((_, i) => (
            <div key={i} className="xc-ml-card">
              <div className="xc-ml-card__thumb">
                <div className="xc-skeleton" style={{ width: "100%", height: "100%" }} />
              </div>
              <div className="xc-ml-card__body">
                <div className="xc-skeleton" style={{ width: "70%", height: 12 }} />
                <div className="xc-skeleton" style={{ width: "40%", height: 10 }} />
              </div>
            </div>
          ))}
        </div>
      ) : totalEntries === 0 ? (
        <div className="xc-ml-empty">
          <i className="ri-image-2-line" aria-hidden="true" />
          <span className="xc-ml-empty__title">No media found</span>
          <span className="xc-ml-empty__text">
            {search
              ? "Try a different search term."
              : "Upload an image from the Upload Device tab to get started."}
          </span>
        </div>
      ) : (
        <>
          <div className="xc-ml-grid">
            {paginated.map((media) => (
              <MediaCard
                key={media.mediaAssetId || media.mediaUrl}
                media={media}
                highlighted={
                  highlightId != null &&
                  String(media.mediaAssetId) === String(highlightId)
                }
                onSelect={onSelect}
              />
            ))}
          </div>

          <MediaLibraryPaginationBar
            showPageSize={false}
            currentPage={page}
            totalPages={totalPages}
            totalEntries={totalEntries}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default RecentUploadsTab;
